import type { ExportColumn, ExportRow } from '@/lib/export';
import type { GroupNode } from '@/lib/item-groups-api';

/**
 * EKSPORT GRUP ARTYKUŁÓW DO PLIKU PŁASKIEGO — zamówione 27.10.2026: „prośba o dodanie możliwości
 * eksportu danych do pliku płaskiego z tej zakładki jak w pozostałych zakładkach np. «Artykuły»".
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ DRZEWO SPŁASZCZONE DO WIERSZY: jeden wiersz na grupę, pod nim po jednym na każdą jej
 *  podgrupę, a w wierszu podgrupy POWTÓRZONY kod i nazwa grupy nadrzędnej. Plik płaski bez tego
 *  powtórzenia nie da się filtrować ani sortować w arkuszu — podgrupa bez rodzica w tym samym
 *  wierszu traci przynależność przy pierwszym sortowaniu.
 *
 *  ⚠️ KLUCZ PODGRUPY TO `ISGID`, NIE SAM KOD — osobna kolumna, bo to on stoi w `Items.ITEMSUBGROUP`.
 *  Goły kod podgrupy nie jest unikalny poza swoją grupą, więc sam w pliku nie wskazuje wiersza SPS.
 *
 *  ⚠️ LICZBY TE SAME CO NA EKRANIE: przy grupie „automaty" to automaty całego poddrzewa, przy
 *  podgrupie — jej własne. Plik, który liczy inaczej niż ekran, odpowiada na inne pytanie.
 *  Eksport bierze to, co ekran ma wczytane — z tą samą szukajką i tym samym „pokaż puste".
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function eksportGrupArtykulow(
  groups: readonly GroupNode[],
  t: (klucz: string) => string,
): { cols: ExportColumn[]; rows: ExportRow[] } {
  const cols: ExportColumn[] = [
    { key: 'groupCode', header: t('igrp.code') },
    { key: 'groupName', header: t('igrp.exp.groupName') },
    { key: 'subCode', header: t('igrp.subCode') },
    { key: 'isgid', header: t('igrp.exp.isgid') },
    { key: 'subName', header: t('igrp.exp.subName') },
    { key: 'items', header: t('igrp.exp.items') },
    { key: 'itemsSubtree', header: t('igrp.exp.itemsSubtree') },
    { key: 'machines', header: t('igrp.exp.machines') },
    { key: 'rationing', header: t('igrp.exp.rationing') },
  ];

  const rows: ExportRow[] = groups.flatMap((g) => [
    {
      groupCode: g.code,
      groupName: g.name,
      subCode: '',
      isgid: '',
      subName: '',
      items: g.items,
      itemsSubtree: g.subtreeItems,
      machines: g.subtreeMachines,
      rationing: g.rationingRules,
    },
    /** ⚠️ Limitów i „z podgrupami" przy podgrupie nie ma — SPS limituje tylko grupę główną. */
    ...g.subGroups.map((s) => ({
      groupCode: g.code,
      groupName: g.name,
      subCode: s.code,
      isgid: s.isgid ?? '',
      subName: s.name,
      items: s.items,
      itemsSubtree: '',
      machines: s.machines,
      rationing: '',
    })),
  ]);

  return { cols, rows };
}
